import { useEffect, useState } from 'react';
import { ArrowLeft, Search, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { useChain } from '../context/ChainContext';
import McFirstBuys from '../components/McFirstBuys';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /firstbuys — scatter plot of each wallet's first KROWN buy time vs avg
 * buy price. Lets the user visually spot presellers (early time + low
 * price) and whales (large bubble = high total bought).
 *
 * Dots are clickable → Address Overview.
 */
export default function FirstBuysPage() {
  const { chain, chainParam, chainInfo } = useChain();
  const isKrown = !chainParam || chain === 'krown';
  const [data, setData] = useState([]);
  const [days, setDays] = useState(90);
  const [minUsd, setMinUsd] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/first-buys`, {
        params: { days_back: days, limit: 500, min_usd: minUsd },
      });
      const pts = (r.data?.points || []).map(p => ({
        ...p,
        // X axis: timestamp millis
        ts: new Date(p.first_buy_time).getTime(),
        // Y axis: avg price in micro-USD for readability
        priceMicros: p.avg_buy_price_usd * 1e6,
        size: Math.max(20, Math.min(300, Math.log(1 + p.total_bought_usd) * 30)),
      }));
      setData(pts);
    } catch (e) {
      setError(e?.message || 'Failed to load first buys');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isKrown) fetchData(); }, [days, minUsd, isKrown]);

  const formatTs = (ts) => {
    const d = new Date(ts);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Non-Krown chains: approximate recent-buyers view from public pool trades.
  if (!isKrown) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="first-buys-page-nonkrown">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
              <Search className="w-6 h-6 text-primary" /> First Buys · Recent Buyers
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Recent buyers on <span className="text-foreground font-medium">{chainInfo?.label || chain}</span> — bubble size = total USD bought.
            </p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="first-buys-back-nonkrown">
              <ArrowLeft className="w-4 h-4 mr-1" /> Explorer
            </Button>
          </Link>
        </div>
        <McFirstBuys key={chain} chain={chain} chainInfo={chainInfo} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="first-buys-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Search className="w-6 h-6 text-amber-500" /> First Buys Timeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Each dot = one wallet's first KROWN buy. Bubble size = total USD bought.
            Look bottom-left for presellers (early + cheap).
          </p>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="first-buys-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </Link>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-3 border-b border-slate-800">
          <CardTitle className="text-sm text-slate-300 flex items-center gap-3 flex-wrap">
            <span>Window:</span>
            {[30, 90, 180, 365].map(d => (
              <Button
                key={d} size="sm"
                variant={days === d ? 'default' : 'outline'}
                className={days === d ? 'bg-amber-600 hover:bg-amber-500 text-slate-950 h-7 text-xs' : 'border-slate-700 text-slate-300 h-7 text-xs'}
                onClick={() => setDays(d)}
                data-testid={`first-buys-window-${d}`}
              >{d}d</Button>
            ))}
            <span className="ml-4">Min total $:</span>
            <Input
              type="number"
              value={minUsd}
              onChange={e => setMinUsd(Number(e.target.value))}
              className="w-24 h-7 text-xs bg-slate-800 border-slate-700"
              data-testid="first-buys-min-usd"
            />
            <Button size="sm" variant="outline" onClick={fetchData} className="border-slate-700 text-slate-300 h-7 text-xs ml-auto" data-testid="first-buys-refresh">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {error && (
            <p className="text-sm text-red-400" data-testid="first-buys-error">{error}</p>
          )}
          {loading ? (
            <Skeleton className="h-[500px] w-full" />
          ) : data.length === 0 ? (
            <p className="text-sm text-slate-400">No data in this window.</p>
          ) : (
            <div data-testid="first-buys-chart">
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 50 }}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="ts" type="number" name="First buy"
                    domain={['dataMin', 'dataMax']}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    tickFormatter={(v) => new Date(v).toLocaleDateString()}
                    label={{ value: 'First buy time', position: 'bottom', offset: 25, fill: '#94a3b8' }}
                  />
                  <YAxis
                    dataKey="priceMicros" type="number" name="Avg buy price"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    tickFormatter={(v) => `$${(v / 1e6).toFixed(6)}`}
                    label={{ value: 'Avg buy price (USD)', angle: -90, position: 'left', offset: 10, fill: '#94a3b8' }}
                  />
                  <ZAxis dataKey="size" range={[20, 300]} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                    formatter={(value, key) => {
                      if (key === 'priceMicros') return [`$${(value / 1e6).toFixed(8)}`, 'Avg buy price'];
                      if (key === 'ts') return [formatTs(value), 'First buy'];
                      return value;
                    }}
                    labelFormatter={() => ''}
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload;
                      return (
                        <div className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200">
                          <div className="font-mono">{p.wallet.slice(0, 10)}…{p.wallet.slice(-6)}</div>
                          <div>First: <span className="text-amber-400">{formatTs(p.ts)}</span></div>
                          <div>Avg price: <span className="text-amber-400">${p.avg_buy_price_usd.toFixed(8)}</span></div>
                          <div>Bought: <span className="text-amber-400">${p.total_bought_usd.toFixed(2)}</span> ({p.buys} buys)</div>
                        </div>
                      );
                    }}
                  />
                  <Scatter
                    data={data}
                    fill="#f59e0b"
                    fillOpacity={0.6}
                    onClick={(d) => { if (d?.wallet) window.location.href = `/address/${d.wallet}`; }}
                    style={{ cursor: 'pointer' }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
              <p className="text-xs text-slate-500 mt-3">
                Showing <b>{data.length}</b> wallets. Click any dot to open the Address Overview.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
